import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { toast } from "sonner";
import { X, RotateCcw, Trash2 } from "lucide-react";
import ConfirmDialog from "./ConfirmDialog";

import { eur } from "@/lib/format";

export default function CestinoDialog({ onClose }) {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["pratiche-cestino"], queryFn: async () => (await api.get("/pratiche/cestino")).data });
  const [permTarget, setPermTarget] = useState(null);
  const items = data || [];

  const restore = async (p) => {
    try {
      await api.post(`/pratiche/${p.id}/ripristina`);
      toast.success("Pratica ripristinata");
      qc.invalidateQueries({ queryKey: ["pratiche-cestino"] });
      qc.invalidateQueries({ queryKey: ["pratiche"] });
    } catch { toast.error("Ripristino non riuscito"); }
  };

  const permDelete = async () => {
    try {
      await api.delete(`/pratiche/${permTarget.id}/definitivo`);
      toast.success("Pratica eliminata definitivamente");
      qc.invalidateQueries({ queryKey: ["pratiche-cestino"] });
      qc.invalidateQueries({ queryKey: ["contabilita"] });
    } catch { toast.error("Eliminazione non riuscita"); }
    finally { setPermTarget(null); }
  };

  return (
    <div className="fixed inset-0 z-[65] bg-[#1a1a1a]/50 flex items-center justify-center p-4" data-testid="cestino-dialog">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-2xl p-4 border-t-2 border-[#1A2B4A] max-h-[85vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-1">
          <h3 className="font-serif text-2xl text-[#1A2B4A] flex items-center gap-2"><Trash2 className="w-5 h-5 text-[#9b2c2c]" /> Cestino pratiche</h3>
          <button data-testid="cestino-close" onClick={onClose} className="text-[#4a5568] hover:text-[#1A2B4A]"><X className="w-5 h-5" /></button>
        </div>
        <p className="text-xs text-[#4a5568] mb-5">Le pratiche eliminate restano qui <b>30 giorni</b>, poi vengono rimosse definitivamente. Ripristinandole tornano nell'elenco.</p>
        {isLoading ? <p className="text-sm text-[#4a5568]">Carico…</p> :
         items.length === 0 ? <p className="text-sm text-[#4a5568]" data-testid="cestino-empty">Il cestino è vuoto.</p> : (
          <div className="space-y-2">
            {items.map((p) => (
              <div key={p.id} data-testid={`cestino-item-${p.id}`} className="flex items-center gap-3 border border-[#1A2B4A]/10 rounded-sm px-3 py-2.5">
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-[#1A2B4A] truncate">{p.cliente ? `${p.cliente.nome} ${p.cliente.cognome}` : "—"}</div>
                  <div className="text-xs text-[#4a5568] truncate">{p.tipo_pratica_nome} · residuo {eur((p.importo_totale || 0) - (p.importo_pagato || 0))}</div>
                </div>
                <span className="text-[11px] text-[#9b2c2c] shrink-0 whitespace-nowrap" title="Giorni prima dell'eliminazione definitiva">tra {p.giorni_residui}g</span>
                <button data-testid={`cestino-restore-${p.id}`} onClick={() => restore(p)}
                  className="inline-flex items-center gap-1 text-xs border border-[#276749]/40 text-[#276749] px-2.5 py-1.5 rounded-sm hover:bg-[#f0f6f2]"><RotateCcw className="w-3.5 h-3.5" /> Ripristina</button>
                <button data-testid={`cestino-perm-${p.id}`} onClick={() => setPermTarget(p)} title="Elimina definitivamente"
                  className="p-1.5 rounded-sm text-[#9b2c2c] hover:bg-[#f7e9e9]"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        )}
      </div>
      {permTarget && (
        <ConfirmDialog title="Eliminare definitivamente?"
          message={`La pratica "${permTarget.tipo_pratica_nome}"${permTarget.cliente ? ` di ${permTarget.cliente.nome} ${permTarget.cliente.cognome}` : ""} e i movimenti/documenti collegati verranno eliminati per sempre.\nQuesta azione non è reversibile.`}
          confirmLabel="Elimina per sempre" onConfirm={permDelete} onClose={() => setPermTarget(null)} />
      )}
    </div>
  );
}
